import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { IHeaderParams } from '@ag-grid-community/core'

export type SelectionMode = 'none' | 'page' | 'all'

interface ThreeStateHeaderCheckboxParams extends IHeaderParams {
  selectionMode: SelectionMode
  onHeaderCheckboxClicked: () => void
}

export default function ThreeStateHeaderCheckbox({
  selectionMode,
  onHeaderCheckboxClicked,
  displayName,
  column,
  progressSort,
  enableSorting,
}: ThreeStateHeaderCheckboxParams) {
  const checkboxRef = useRef<HTMLInputElement>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    () => column.getSort() ?? null
  )

  useEffect(() => {
    const el = checkboxRef.current
    if (!el) return
    el.checked = selectionMode === 'page'
    el.indeterminate = selectionMode === 'all'
  }, [selectionMode])

  useEffect(() => {
    const handleSortChanged = () => setSortDirection(column.getSort() ?? null)
    column.addEventListener('sortChanged', handleSortChanged)
    return () => column.removeEventListener('sortChanged', handleSortChanged)
  }, [column])

  const handleLabelClick = useCallback(() => {
    if (enableSorting) progressSort(false)
  }, [enableSorting, progressSort])

  return (
    <div className="ag-cell-label-container" role="presentation">
      <div
        className="ag-header-cell-label"
        role="presentation"
        onClick={enableSorting ? handleLabelClick : undefined}
        style={enableSorting ? { cursor: 'pointer' } : undefined}
      >
        {sortDirection === 'asc' && (
          <span className="ag-header-icon ag-sort-ascending-icon">
            <span className="ag-icon ag-icon-asc" role="presentation" />
          </span>
        )}
        {sortDirection === 'desc' && (
          <span className="ag-header-icon ag-sort-descending-icon">
            <span className="ag-icon ag-icon-desc" role="presentation" />
          </span>
        )}
        <span className="ag-header-cell-text">{displayName}</span>
      </div>
      <div className="ag-header-select-all" role="presentation">
        <div className="ag-labeled ag-label-align-right ag-checkbox ag-input-field">
          <div
            className={[
              'ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper',
              selectionMode === 'page' ? 'ag-checked' : '',
              selectionMode === 'all' ? 'ag-indeterminate' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            role="presentation"
          >
            <input
              ref={checkboxRef}
              type="checkbox"
              className="ag-input-field-input ag-checkbox-input"
              aria-label="Select rows"
              tabIndex={-1}
              onChange={onHeaderCheckboxClicked}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
